import { useT } from '../lib/i18n';
import { useDialog } from './Dialog';
import { ChevronIcon, CloseIcon } from './Icons';
import { holdModal } from '../lib/modal';
import { Layer } from './Layer';
import { useCallback, useEffect, useRef, type ReactNode } from 'react';

/**
 * One of many, with a way to the next one.
 *
 * A panel that opens on a single frame of eighty is a dead end: you look, you
 * close it, you find the next frame with your eyes, you open that. Given this,
 * the panel gets chevrons in its header and answers the arrow keys, and going
 * through a sheet becomes one gesture repeated.
 */
export interface Step {
  /** One-based, for the reader. */
  index: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
}

interface Props {
  title: string;
  /** Short line under the title. */
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** Two-column layout for inspecting a frame beside its readouts. */
  wide?: boolean;
  /**
   * Set while the sheet holds unsaved work. Closing then asks first, instead
   * of silently discarding everything that was typed.
   */
  dirty?: boolean;
  /** Extra controls in the header, left of Close. */
  headerAction?: ReactNode;
  /** Set when the panel is showing one of a series it can move through. */
  step?: Step;
}

export function Sheet({
  title,
  subtitle,
  onClose,
  children,
  footer,
  wide,
  dirty,
  headerAction,
  step,
}: Props) {
  const panel = useRef<HTMLDivElement>(null);
  // Where the pointer went *down*. A click event fires on the nearest common
  // ancestor of press and release, so selecting text in a field and releasing
  // past the panel edge would otherwise register as a backdrop click — and
  // throw away everything typed.
  const pressedOnBackdrop = useRef(false);

  // Held in a ref so the effect below runs once. The parent usually passes an
  // inline arrow, and re-running on every keystroke thrashes the listener and
  // rewrites body styles, which is what made the sheet feel sluggish.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  // Held in a ref for the same reason as onClose: the escape-key effect must
  // not re-bind every time the translator identity changes.
  const t = useT();
  const { ask } = useDialog();
  const tRef = useRef(t);
  tRef.current = t;
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  const askRef = useRef(ask);
  askRef.current = ask;

  const stepRef = useRef(step);
  stepRef.current = step;

  const requestClose = useCallback(async () => {
    if (dirtyRef.current) {
      const discard = await askRef.current({
        title: tRef.current('sheet.discardTitle'),
        body: tRef.current('sheet.discardConfirm'),
        confirmLabel: tRef.current('common.discardIt'),
        danger: true,
      });
      if (!discard) return;
    }
    closeRef.current();
  }, []);

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const hold = holdModal();

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Not while a confirm is up over this sheet: that press is the dialog's.
        if (!hold.isTop()) return;
        event.preventDefault();
        void requestClose();
        return;
      }
      /*
       * Left and right move through the series — unless the caret is in a
       * field, where they are how you move through the text. A panel that
       * jumped to the next frame while someone was correcting a name would
       * be worse than one that could not page at all.
       */
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        const inField = (document.activeElement as HTMLElement | null)?.closest(
          'input, textarea, select, [contenteditable]',
        );
        if (inField || !stepRef.current) return;
        event.preventDefault();
        (event.key === 'ArrowLeft' ? stepRef.current.onPrev : stepRef.current.onNext)();
        return;
      }
      // Keep tabbing inside the dialog.
      if (event.key !== 'Tab' || !panel.current) return;
      const focusable = panel.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable.length) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panel.current?.focus();

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
      hold();
      previouslyFocused?.focus?.();
    };
  }, [requestClose]);

  return (
    <Layer>
      <div
        className="sheet"
        onPointerDown={(event) => {
          pressedOnBackdrop.current = event.target === event.currentTarget;
        }}
        onClick={(event) => {
          // Both the press and the release have to be on the backdrop.
          if (event.target === event.currentTarget && pressedOnBackdrop.current) void requestClose();
          pressedOnBackdrop.current = false;
        }}
      >
        <div
          className={`sheet__panel ${wide ? 'sheet__panel--wide' : ''}`}
          role="dialog"
          aria-modal="true"
          aria-label={title}
          tabIndex={-1}
          ref={panel}
        >
          <div className="sheet__head">
            <div className="sheet__title">
              <h2>{title}</h2>
              {subtitle && <p className="sheet__sub">{subtitle}</p>}
            </div>

            {step && step.total > 1 && (
              <div className="sheet__step">
                <button
                  type="button"
                  className="sheet__arrow"
                  aria-label={t('sheet.previous')}
                  onClick={step.onPrev}
                >
                  <ChevronIcon />
                </button>
                <span className="sheet__count">
                  {step.index} / {step.total}
                </span>
                <button
                  type="button"
                  className="sheet__arrow"
                  aria-label={t('sheet.next')}
                  onClick={step.onNext}
                >
                  <ChevronIcon />
                </button>
              </div>
            )}

            {headerAction}
            {/* An icon, not the word: the word was the widest thing in the
              header and said what every × on every panel already says. */}
            <button
              type="button"
              className="sheet__close"
              aria-label={t('common.close')}
              title={t('common.close')}
              onClick={() => void requestClose()}
            >
              <CloseIcon />
            </button>
          </div>

          <div className="sheet__body">{children}</div>
          {footer && <div className="sheet__foot">{footer}</div>}
        </div>
      </div>
    </Layer>
  );
}
